import React from 'react';
import styled from 'styled-components';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import ContributionMappingHeading from './ContributionMappingHeading';
import ContributionMappingRows from './ContributionMappingRows';
import ContributionMappingRow from './ContributionMappingRow';
import { getColumnMapping } from './worker';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';

interface ContributionMappingProps {
  data: string[][] | undefined;
  columns: ReturnType<typeof getColumnMapping> | undefined;
  moveToFileStep: () => unknown;
  moveToMappingStep: () => unknown;
  moveToTableStep: () => unknown;
}

const StyledDiv = styled.div`
  margin-bottom: ${SPACERS.extraLarge};
  .mapping-content-heading {
    max-width: 669px;
    margin-left: auto;
    margin-right: auto;
    text-align: center;
    .mapping-heading {
      margin-bottom: ${SPACERS.small};
    }
    .mapping-subheading {
      margin-bottom: ${SPACERS.large};
    }
    .mapping-button-wrapper {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: ${SPACERS.small};
      margin-bottom: ${SPACERS.extraLarge};
    }
  }
  .mapping-table {
    max-width: 1018px;
    margin-left: auto;
    margin-right: auto;
    .mapping-file-error-message {
      text-align: center;
      margin-top: ${SPACERS.large};
    }
  }
`;

const ContributionMapping: React.FC<ContributionMappingProps> = ({
  data,
  columns,
  moveToFileStep,
  moveToTableStep,
}) => {
  if (!columns || !data) {
    return <TrueImpactLoading />;
  }

  const inValidFile = data.length < 2;
  const isColumnsValid = columns.missingColumns.length === 0;
  return (
    <StyledDiv>
      <div className="mapping-content-heading">
        <TrueImpactText variant="heading-page" className="mapping-heading">
          Please map your CSV fields to the appropriate True Impact import field
        </TrueImpactText>
        <TrueImpactText className="mapping-subheading">
          We’ll create a donation for each row and place it in it’s designated
          portfolio.
        </TrueImpactText>
        <div className="mapping-button-wrapper">
          <TrueImpactButton variant="secondary-large" onClick={moveToFileStep}>
            Upload different file
          </TrueImpactButton>
          <TrueImpactButton
            onClick={moveToTableStep}
            disabled={inValidFile || !isColumnsValid}
          >
            Next
          </TrueImpactButton>
        </div>
      </div>
      <div className="mapping-table">
        <ContributionMappingRow
          columnOne={
            <ContributionMappingHeading
              heading="CSV Field"
              subheading="The fields that were imported from your CSV file."
            />
          }
          columnThree={
            <ContributionMappingHeading
              heading="True Impact Field"
              subheading="The fields that will be mapped in the True Impact system from your CSV file."
            />
          }
        />
        {inValidFile ? (
          <TrueImpactText
            color="critical"
            className="mapping-file-error-message"
          >
            Looks like your file is empty. Please try another file.
          </TrueImpactText>
        ) : (
          <ContributionMappingRows data={data} columns={columns} />
        )}
      </div>
    </StyledDiv>
  );
};

export default ContributionMapping;
